import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { monthLabels } from "../styles/colors";
import { APP_ROUTES } from "../utils/appRoutes";
import { cn } from "../utils/cn";
import type { DashboardTimelineDream } from "../store/dashboardSlice";
import { Button } from "./ui";

const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const ALL_TAB = "All";

function GoalIndicator({ count, month, titles, allCompleted }: { count: number; month: number; titles: string[]; allCompleted?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const leftPercent = ((month - 0.5) / 12) * 100;
  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setOpen((v) => !v);
  };

  return (
    <div
      ref={ref}
      className="absolute inset-y-0 flex items-center"
      style={{ left: `${leftPercent}%`, transform: "translateX(-50%)", zIndex: 10 }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={count === 1 ? "1 goal starts this month" : `${count} goals start this month`}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggle(e); }}
        className="relative flex items-center justify-center cursor-pointer"
        style={{ padding: "8px 10px", margin: "-8px -10px" }}
      >
        {allCompleted ? (
          <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full px-1" style={{ background: "#22c55e" }} aria-label="All goals completed">
            <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <polyline points="1.5,5 4,7.5 8.5,2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {count > 1 && <span className="ml-0.5 text-[9px] font-bold leading-none text-white">{count}</span>}
          </span>
        ) : count === 1 ? (
          <svg width="10" height="13" viewBox="0 0 10 13" fill="none" aria-hidden="true">
            <line x1="2" y1="1" x2="2" y2="12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M2 1.5 L9 4 L2 6.5Z" fill="#475569" />
          </svg>
        ) : (
          <span className="flex h-5 min-w-[28px] items-center justify-center gap-0.5 rounded-full bg-slate-600 px-1.5 text-[9px] font-bold leading-none text-white select-none">
            <svg width="7" height="9" viewBox="0 0 10 13" fill="none" aria-hidden="true" className="shrink-0">
              <line x1="2" y1="1" x2="2" y2="12" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M2 1.5 L9 4 L2 6.5Z" fill="white" />
            </svg>
            {count}
          </span>
        )}

        {open && (
          <div
            className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-xl border border-slate-100 bg-white shadow-xl"
            style={{ minWidth: 300, maxWidth: 400 }}
          >
            <p className="px-5 pb-1 pt-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {count} goal{count > 1 ? "s" : ""} this month
            </p>
            <ul className="m-0 list-none p-0 pb-3 pt-1">
              {(titles.length ? titles : Array(count).fill("Untitled goal")).map((title, i) => (
                <li key={i} className="flex items-start gap-3 px-5 py-2">
                  <svg width="9" height="11" viewBox="0 0 10 13" fill="none" className="mt-0.5 shrink-0" aria-hidden="true">
                    <line x1="2" y1="1" x2="2" y2="12" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M2 1.5 L9 4 L2 6.5Z" fill="#475569" />
                  </svg>
                  <span className="text-[13px] font-medium leading-snug text-slate-700">{title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

const buildVisibleRows = (timelineData: DashboardTimelineDream[], year: number) => {
  return (timelineData || [])
    .map((dream) => {
      const startYear = Number(dream.startYear);
      const endYear = Number(dream.targetYear);
      const startMonth = Number(dream.startMonth);
      const endMonth = Number(dream.targetMonth);

      if (startYear > year || endYear < year) return null;

      const clampedStartMonth = startYear < year ? 1 : Math.min(12, Math.max(1, startMonth));
      const clampedEndMonth = endYear > year ? 12 : Math.min(12, Math.max(1, endMonth));
      if (clampedEndMonth < clampedStartMonth) return null;

      return {
        id: dream.id,
        title: dream.title || "Untitled dream",
        color: dream.color || "#8B5CF6",
        category: dream.category || "Personal",
        leftPercent: ((clampedStartMonth - 1) / 12) * 100,
        widthPercent: ((clampedEndMonth - clampedStartMonth + 1) / 12) * 100,
        completedPercent: dream.completedPercent,
        goalsByMonth: (dream.goalsByMonth || []).filter((g) => g.year === year),
        extendsLeft: startYear < year,
        extendsRight: endYear > year,
      };
    })
    .filter(Boolean);
};

const DreamTimeline = ({
  timelineData = [],
  loading = false,
  error = null,
  lockedDreamIds = new Set<string>(),
  onLockedClick,
}: {
  timelineData?: DashboardTimelineDream[];
  loading?: boolean;
  error?: string | null;
  lockedDreamIds?: Set<string>;
  onLockedClick?: () => void;
}) => {
  const navigate = useNavigate();
  const currentYear = dayjs().year();
  const [activeYear, setActiveYear] = useState<number>(currentYear);
  const [activeTab, setActiveTab] = useState<string>(ALL_TAB);

  const allRows = useMemo(
    () => buildVisibleRows(timelineData, activeYear),
    [timelineData, activeYear],
  );

  const categories = useMemo(() => {
    const seen = new Set<string>();
    return allRows
      .map((r) => r!.category)
      .filter((c) => { if (seen.has(c)) return false; seen.add(c); return true; });
  }, [allRows]);

  const rows = useMemo(
    () => activeTab === ALL_TAB ? allRows : allRows.filter((r) => r!.category === activeTab),
    [allRows, activeTab],
  );

  const [drawerRow, setDrawerRow] = useState<typeof rows[number] | null>(null);

  const handleOpenPlan = (dreamId) => {
    if (!dreamId) {
      navigate(APP_ROUTES.plan);
      return;
    }
    navigate(APP_ROUTES.dreamDetail(String(dreamId)));
  };

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5"
      data-testid="dashboard-dream-timeline"
    >
      <div className="mb-4 flex flex-wrap items-center gap-3 justify-between md:gap-4">
        <div className="flex items-center gap-3">
          <h3 className="m-0 text-[16px] font-bold leading-none text-slate-900 md:text-xl">Dreams Roadmap {activeYear}</h3>
          <div className="flex items-center gap-3 leading-none">
            <button
              type="button"
              onClick={() => setActiveYear((y) => y - 1)}
              aria-label="Previous year"
              data-testid="dream-timeline-prev-year"
              className="inline-flex items-center justify-center border-0 bg-transparent p-0 leading-none text-slate-400 transition-colors hover:text-slate-700 focus:outline-none focus-visible:text-slate-700"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <polyline points="15 5 8 12 15 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setActiveYear((y) => y + 1)}
              aria-label="Next year"
              data-testid="dream-timeline-next-year"
              className="inline-flex items-center justify-center border-0 bg-transparent p-0 leading-none text-slate-400 transition-colors hover:text-slate-700 focus:outline-none focus-visible:text-slate-700"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <polyline points="9 5 16 12 9 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          {activeYear !== currentYear && (
            <button
              type="button"
              onClick={() => setActiveYear(currentYear)}
              data-testid="dream-timeline-reset-year"
              aria-label={`Jump to ${currentYear}`}
              className="rounded-full border-0 bg-brand-overlay px-2.5 py-0.5 text-[11px] font-semibold text-brand-primary transition-colors hover:bg-brand-primary hover:text-white"
            >
              {currentYear}
            </button>
          )}
        </div>
        {/* Category tabs + Goal legend */}
        <div className="ml-auto flex items-center gap-2">
          {!loading && !error && categories.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {[ALL_TAB, ...categories].map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    aria-pressed={isActive}
                    data-testid={`dream-timeline-tab-${tab.toLowerCase()}`}
                    className={cn(
                      "rounded-full border-0 px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors",
                      isActive
                        ? "bg-brand-primary text-white shadow-sm hover:bg-brand-primaryDark"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-brand-primary",
                    )}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div
          className="flex h-40 items-center justify-center rounded-lg bg-slate-50"
          data-testid="dream-timeline-loading"
        >
          <p className="text-sm font-semibold text-slate-500">Loading timeline...</p>
        </div>
      ) : error ? (
        <div
          className="rounded-lg bg-red-50 px-4 py-5 text-sm font-medium text-red-600"
          data-testid="dream-timeline-error"
        >
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="mb-5 opacity-25">
            <path d="M26 6 L30.5 18.5 L44 18.5 L33.5 26.5 L37.5 39 L26 31.5 L14.5 39 L18.5 26.5 L8 18.5 L21.5 18.5 Z" stroke="#64748B" strokeWidth="2.5" strokeLinejoin="round" fill="none"/>
            <circle cx="26" cy="6" r="2" fill="#64748B" opacity="0.5"/>
            <circle cx="44" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
            <circle cx="8" cy="18.5" r="1.5" fill="#64748B" opacity="0.4"/>
          </svg>
          <p className="m-0 text-[16px] font-bold text-slate-700">No dreams yet</p>
          <p className="m-0 mt-1.5 text-[13px] text-slate-400">Create your first dream to start planning milestones</p>
          <Button variant="primary" size="md" className="mt-6" onClick={() => navigate(APP_ROUTES.dreamCreate)}>
            + New dream
          </Button>
        </div>
      ) : (
        <>
          {/* ── MOBILE card list (hidden on md+) ───────────────────── */}
          <div className="flex flex-col gap-2 md:hidden">
            {rows.map((row) => {
              const isLocked = lockedDreamIds.has(row!.id);
              return (
              <button
                key={row!.id || row!.title}
                type="button"
                onClick={() => isLocked ? onLockedClick?.() : setDrawerRow(row)}
                className="relative flex w-full items-center gap-3 rounded-xl border-0 px-4 py-3 text-left transition-colors"
                style={{
                  background: "#fafafa",
                  border: `1px solid #e2e8f0`,
                  cursor: isLocked ? "not-allowed" : "pointer",
                  filter: isLocked ? "blur(2px)" : undefined,
                  opacity: isLocked ? 0.6 : 1,
                  userSelect: isLocked ? "none" : undefined,
                }}
              >
                <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: row!.color }} />
                <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-slate-800">{row!.title}</span>
                {isLocked ? (
                  <svg width="11" height="13" viewBox="0 0 11 13" fill="none" className="flex-shrink-0">
                    <rect x="1" y="5" width="9" height="7.5" rx="1.2" stroke="#94A3B8" strokeWidth="1.2"/>
                    <path d="M3.5 5V3.5a2 2 0 1 1 4 0V5" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                ) : (
                  <>
                    <span className="flex-shrink-0 text-[13px] font-bold" style={{ color: row!.color }}>{row!.completedPercent}%</span>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0 text-slate-300">
                      <path d="M5 2L10 7L5 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              );
            })}
          </div>

          {/* ── DESKTOP grid (hidden on mobile) ────────────────────── */}
          <div className="hidden grid-cols-[180px_1fr] gap-x-3 md:grid">
            <div />
            <div className="mb-3 grid grid-cols-12 border-b border-slate-100 pb-3">
              {monthLabels.map((month) => (
                <span key={month} className="text-center text-[10px] font-bold tracking-wide text-slate-400">
                  {month}
                </span>
              ))}
            </div>
            {rows.map((row) => {
              const isLocked = lockedDreamIds.has(row!.id);
              return (
              <React.Fragment key={row!.id || row!.title}>
                <div
                  className="flex flex-col justify-center gap-1 pr-3 pb-2"
                  style={isLocked ? { filter: "blur(3px)", pointerEvents: "none", userSelect: "none", opacity: 0.6 } : undefined}
                >
                  <p className="m-0 truncate text-sm font-semibold text-slate-800">{row!.title}</p>
                  <div className="flex items-center gap-2">
                    <div className="relative h-[6px] flex-1 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                        style={{ width: `${row!.completedPercent}%`, backgroundColor: row!.color }}
                      />
                    </div>
                    <span className="w-7 shrink-0 text-right text-[11px] font-bold" style={{ color: row!.color }}>
                      {row!.completedPercent}%
                    </span>
                  </div>
                </div>
                <div
                  role="button"
                  tabIndex={isLocked ? -1 : 0}
                  onClick={() => isLocked ? onLockedClick?.() : handleOpenPlan(row!.id)}
                  onKeyDown={(e) => { if (!isLocked && (e.key === "Enter" || e.key === " ")) handleOpenPlan(row!.id); }}
                  className="relative mb-3 h-10 w-full rounded-md bg-slate-100"
                  style={isLocked ? { cursor: "not-allowed" } : { cursor: "pointer" }}
                  aria-label={isLocked ? `${row!.title} — Premium only` : `Open plan for ${row!.title}`}
                >
                  {Array.from({ length: 11 }, (_, i) => (
                    <div key={i} className="absolute inset-y-0 w-px bg-slate-200" style={{ left: `${((i + 1) / 12) * 100}%` }} />
                  ))}
                  {!isLocked && (
                    <div
                      className="absolute inset-y-1.5 flex items-center"
                      style={{
                        left: `${row!.leftPercent}%`,
                        width: `${row!.widthPercent}%`,
                        background: `${row!.color}22`,
                        borderLeft: row!.extendsLeft ? "none" : `3px solid ${row!.color}`,
                        borderRight: row!.extendsRight ? "none" : undefined,
                        borderRadius: row!.extendsLeft && row!.extendsRight ? 0 : row!.extendsLeft ? "0 6px 6px 0" : row!.extendsRight ? "6px 0 0 6px" : 6,
                      }}
                    >
                      {row!.extendsLeft && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full flex items-center" title={`Started before ${activeYear}`}>
                          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><polyline points="8,2 2,7 8,12" stroke={row!.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </span>
                      )}
                      {row!.extendsRight && (
                        <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full flex items-center" title={`Continues into ${activeYear + 1}`}>
                          <svg width="10" height="14" viewBox="0 0 10 14" fill="none"><polyline points="2,2 8,7 2,12" stroke={row!.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </span>
                      )}
                    </div>
                  )}
                  {!isLocked && row!.goalsByMonth.map((g) => (
                    <GoalIndicator key={`${g.year}-${g.month}`} count={g.count} month={g.month} titles={g.titles ?? []} allCompleted={g.allCompleted} />
                  ))}
                  {isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center gap-1.5 rounded-md" style={{ background: "rgba(248,250,252,0.85)" }}>
                      <svg width="11" height="13" viewBox="0 0 11 13" fill="none">
                        <rect x="1" y="5" width="9" height="7.5" rx="1.2" stroke="#94A3B8" strokeWidth="1.2"/>
                        <path d="M3.5 5V3.5a2 2 0 1 1 4 0V5" stroke="#94A3B8" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                      <span className="text-[10px] font-semibold text-slate-400">Premium only</span>
                    </div>
                  )}
                </div>
              </React.Fragment>
              );
            })}
          </div>

          {/* ── Mobile timeline drawer ──────────────────────────────── */}
          {drawerRow && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-[500] bg-black/40"
                onClick={() => setDrawerRow(null)}
                aria-hidden="true"
              />
              {/* Bottom sheet */}
              <div className="fixed bottom-0 left-0 right-0 z-[501] rounded-t-2xl bg-white px-5 pb-8 pt-5 shadow-2xl">
                {/* Handle */}
                <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-slate-200" />

                {/* Header */}
                <div className="mb-1 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: drawerRow!.color }} />
                    <span className="truncate text-[16px] font-bold text-slate-900">{drawerRow!.title}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDrawerRow(null)}
                    className="flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-slate-400 hover:bg-slate-100"
                    aria-label="Close"
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M1.5 1.5L10.5 10.5M10.5 1.5L1.5 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>

                {/* Progress bar */}
                <div className="mb-5 flex items-center gap-2">
                  <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                      style={{ width: `${drawerRow!.completedPercent}%`, backgroundColor: drawerRow!.color }}
                    />
                  </div>
                  <span className="flex-shrink-0 text-[13px] font-bold" style={{ color: drawerRow!.color }}>
                    {drawerRow!.completedPercent}% complete
                  </span>
                </div>

                {/* Month labels */}
                <div className="mb-1 grid grid-cols-12">
                  {MONTH_ABBR.map((m) => (
                    <span key={m} className="text-center text-[8px] font-bold tracking-wide text-slate-400">{m}</span>
                  ))}
                </div>

                {/* Timeline bar */}
                <div className="relative h-10 w-full rounded-md bg-slate-100">
                  {Array.from({ length: 11 }, (_, i) => (
                    <div key={i} className="absolute inset-y-0 w-px bg-slate-200" style={{ left: `${((i + 1) / 12) * 100}%` }} />
                  ))}
                  <div
                    className="absolute inset-y-1.5"
                    style={{
                      left: `${drawerRow!.leftPercent}%`,
                      width: `${drawerRow!.widthPercent}%`,
                      background: `${drawerRow!.color}22`,
                      borderLeft: drawerRow!.extendsLeft ? "none" : `3px solid ${drawerRow!.color}`,
                      borderRight: drawerRow!.extendsRight ? "none" : undefined,
                      borderRadius: drawerRow!.extendsLeft && drawerRow!.extendsRight ? 0 : drawerRow!.extendsLeft ? "0 6px 6px 0" : drawerRow!.extendsRight ? "6px 0 0 6px" : 6,
                    }}
                  />
                  {drawerRow!.goalsByMonth.map((g) => (
                    <GoalIndicator key={`${g.year}-${g.month}`} count={g.count} month={g.month} titles={g.titles ?? []} allCompleted={g.allCompleted} />
                  ))}
                </div>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => { setDrawerRow(null); handleOpenPlan(drawerRow!.id); }}
                  className="mt-5 w-full cursor-pointer rounded-xl border-0 py-3 text-[14px] font-semibold text-white transition-colors"
                  style={{ background: drawerRow!.color }}
                >
                  Open Dream Details
                </button>
              </div>
            </>
          )}
        </>
      )}

    </section>
  );
};

export default DreamTimeline;
